<template>
  <div class="account-page">
    <div class="account-container">
      <div class="card overview-card">
        <div class="profile-section">
          <div class="avatar-wrapper">
            <img src="@/assets/avatar.png" alt="Avatar" class="avatar-img" v-if="hasAvatar" />
            <div class="avatar-placeholder" v-else>{{ firstLetter }}</div>
          </div>
          <div class="user-info">
            <div class="name-row">
              <h3 class="user-name">{{ userStore.userInfo?.username || '未登录' }}</h3>
              <span class="uid-badge">UID: {{ mockData.userId }}</span>
            </div>
            <p class="meta-text">注册时间：{{ mockData.registerTime }}</p>
          </div>
        </div>

        <div class="vertical-divider"></div>

        <div class="asset-section">
          <div class="asset-info">
            <span class="asset-label">当前算力余额</span>
            <span class="number">{{ userStore.userInfo?.balance ?? '--' }}</span>
            <span class="unit">点</span>
          </div>
          <div class="sub-info" v-if="subActive">
            <span class="sub-badge">{{ subLabel }}</span>
            <span class="sub-expire" v-if="!subAdmin">到期：{{ subExpiry }}</span>
          </div>
          <button class="btn btn-primary push-right" @click="router.push('/payment')">获取算力</button>
        </div>
      </div>

      <div class="card redeem-card">
        <div class="redeem-row">
          <div class="redeem-label">
            <span class="redeem-title">兑换码</span>
            <span class="redeem-hint">输入运营发放的活动兑换码，立即获取算力</span>
          </div>
          <div class="redeem-form">
            <input
              v-model="redeemCode"
              class="redeem-input"
              placeholder="请输入兑换码"
              maxlength="8"
              :disabled="redeeming"
              @keyup.enter="handleRedeem"
            />
            <button
              class="btn btn-outline redeem-submit"
              :disabled="redeeming || !redeemCode.trim()"
              @click="handleRedeem"
            >
              {{ redeeming ? '兑换中...' : '兑换' }}
            </button>
          </div>
          <span v-if="redeemMsg" :class="['redeem-msg', redeemOk ? 'ok' : 'err']">{{ redeemMsg }}</span>
        </div>
      </div>

      <div class="card security-card">
        <div class="card-header">
          <h3 class="card-title">安全设置</h3>
          <button class="btn-text" @click="router.push('/docs')">
            <i class="icon-docs"></i> 帮助文档
          </button>
        </div>

        <div class="security-list">
          <div class="list-item">
            <div class="item-main">
              <div class="item-title">账号密码</div>
              <div class="item-desc">用于保护账号信息和登录安全，建议定期修改</div>
            </div>
            <div class="item-action">
              <span :class="['status-indicator', hasPassword ? 'ok' : 'warn']">
                <span class="dot"></span>{{ hasPassword ? '已设置' : '未设置' }}
              </span>
              <button class="btn-outline" @click="openPasswordModal">{{ hasPassword ? '修改密码' : '设置密码' }}</button>
            </div>
          </div>

          <div class="list-item">
            <div class="item-main">
              <div class="item-title">安全手机</div>
              <div class="item-desc">
                <template v-if="phoneDisplay">
                  已绑定手机：<span class="highlight-text">{{ phoneDisplay }}</span>
                </template>
                <template v-else>
                  未绑定，绑定后可用于账号找回和接收通知
                </template>
              </div>
            </div>
            <div class="item-action">
              <span class="status-indicator ok" v-if="phoneDisplay"><span class="dot"></span>已绑定</span>
              <span class="status-indicator warn" v-else><span class="dot"></span>未绑定</span>
              <button class="btn-outline" :disabled="!phoneDisplay">更换手机</button>
            </div>
          </div>

          <div class="list-item">
            <div class="item-main">
              <div class="item-title">安全邮箱</div>
              <div class="item-desc" :class="{ 'text-muted': !mockData.email }">
                {{ mockData.email ? `已绑定邮箱：${mockData.email}` : '未绑定，绑定后可用于找回密码和接收通知' }}
              </div>
            </div>
            <div class="item-action">
              <span class="status-indicator warn" v-if="!mockData.email"><span class="dot"></span>未绑定</span>
              <span class="status-indicator ok" v-else><span class="dot"></span>已绑定</span>
              <button class="btn-primary-outline" v-if="!mockData.email">去绑定</button>
              <button class="btn-outline" v-else>更换邮箱</button>
            </div>
          </div>
        </div>
      </div>

      <div class="actions-section">
        <button class="btn-danger" @click="handleLogout" :disabled="isLoggingOut">
          {{ isLoggingOut ? '正在退出...' : '退出登录' }}
        </button>
      </div>
    </div>

    <div v-if="passwordModalVisible" class="pwd-modal-mask" @click.self="closePasswordModal">
      <div class="pwd-modal">
        <div class="pwd-modal-title">{{ hasPassword ? '修改密码' : '设置密码' }}</div>
        <div class="pwd-modal-hint">密码至少 6 位，建议字母与数字组合</div>
        <div class="pwd-field">
          <label>新密码</label>
          <input
            v-model="newPassword"
            type="password"
            placeholder="请输入新密码"
            autocomplete="new-password"
            :disabled="changingPassword"
            @keyup.enter="focusConfirm"
          />
        </div>
        <div class="pwd-field">
          <label>确认密码</label>
          <input
            ref="confirmRef"
            v-model="confirmPassword"
            type="password"
            placeholder="请再次输入"
            autocomplete="new-password"
            :disabled="changingPassword"
            @keyup.enter="handleChangePassword"
          />
        </div>
        <div v-if="passwordMsg" :class="['pwd-msg', passwordOk ? 'ok' : 'err']">{{ passwordMsg }}</div>
        <div class="pwd-actions">
          <button class="btn-outline" type="button" :disabled="changingPassword" @click="closePasswordModal">取消</button>
          <button class="btn-primary" type="button" :disabled="changingPassword" @click="handleChangePassword">
            {{ changingPassword ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/useAuthStore'
import { httpRequest } from '@/api/httpClient'

const router = useRouter()
const userStore = useAuthStore()

const isLoggingOut = ref(false)

const passwordModalVisible = ref(false)
const newPassword = ref('')
const confirmPassword = ref('')
const passwordMsg = ref('')
const passwordOk = ref(false)
const changingPassword = ref(false)
const confirmRef = ref(null)

const hasPassword = computed(() => !!userStore.userInfo?.hasPassword)
const phoneDisplay = computed(() => userStore.userInfo?.phone || '')

const focusConfirm = () => {
  nextTick(() => confirmRef.value?.focus())
}

const openPasswordModal = () => {
  newPassword.value = ''
  confirmPassword.value = ''
  passwordMsg.value = ''
  passwordOk.value = false
  passwordModalVisible.value = true
}

const closePasswordModal = () => {
  if (changingPassword.value) return
  passwordModalVisible.value = false
}

const handleChangePassword = async () => {
  if (changingPassword.value) return
  const pwd = newPassword.value
  const confirm = confirmPassword.value
  if (!pwd) {
    passwordOk.value = false
    passwordMsg.value = '请输入新密码'
    return
  }
  if (pwd.length < 6) {
    passwordOk.value = false
    passwordMsg.value = '密码至少 6 位'
    return
  }
  if (pwd !== confirm) {
    passwordOk.value = false
    passwordMsg.value = '两次输入的密码不一致'
    return
  }
  changingPassword.value = true
  passwordMsg.value = ''
  try {
    await httpRequest('/api/auth/change-password', {
      method: 'POST',
      body: { newPassword: pwd }
    })
    passwordOk.value = true
    passwordMsg.value = '保存成功'
    await userStore.refreshProfileSilently()
    setTimeout(() => {
      passwordModalVisible.value = false
    }, 800)
  } catch (error) {
    passwordOk.value = false
    passwordMsg.value = error?.message || '保存失败'
  } finally {
    changingPassword.value = false
  }
}

const redeemCode = ref('')
const redeeming = ref(false)
const redeemMsg = ref('')
const redeemOk = ref(false)

const handleRedeem = async () => {
  const code = redeemCode.value.trim()
  if (!code || redeeming.value) return
  redeeming.value = true
  redeemMsg.value = ''
  try {
    const data = await httpRequest('/api/redemption/redeem', {
      method: 'POST',
      body: { code }
    })
    redeemOk.value = true
    redeemMsg.value = `兑换成功，已到账 ${data.credits} 点`
    redeemCode.value = ''
    await userStore.refreshProfileSilently()
  } catch (error) {
    redeemOk.value = false
    redeemMsg.value = error?.message || '兑换失败'
  } finally {
    redeeming.value = false
  }
}

const firstLetter = computed(() => {
  const name = userStore.userInfo?.username
  return name ? name.charAt(0).toUpperCase() : 'U'
})
const hasAvatar = ref(false)

const mockData = ref({
  userId: '100024',
  email: '',
  registerTime: '2026-03-15 10:24:00'
})

const subActive = computed(() => userStore.userInfo?.subscriptionStatus === 'ACTIVE')
const subAdmin = computed(() => userStore.userInfo?.subscriptionPlan === 'ADMIN')
const subLabel = computed(() => {
  const plan = userStore.userInfo?.subscriptionPlan
  if (plan === 'ADMIN') return '管理员'
  if (plan === 'MONTHLY') return '月卡会员'
  if (plan === 'YEARLY') return '年卡会员'
  return '会员'
})
const subExpiry = computed(() => {
  const raw = userStore.userInfo?.subscriptionExpiresAt
  if (!raw) return ''
  try {
    return new Date(raw).toLocaleDateString('zh-CN')
  } catch { return raw }
})

const handleLogout = async () => {
  if (isLoggingOut.value) return
  isLoggingOut.value = true
  try {
    await userStore.logout()
  } catch (error) {
    console.error('登出失败:', error)
  } finally {
    isLoggingOut.value = false
    router.push('/login')
  }
}
</script>

<style scoped>
.account-page {
  padding: 2rem;
  background-color: var(--bg-body);
  min-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.page-header {
  width: 100%;
  max-width: 900px;
  margin-bottom: 1.5rem;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-title);
  margin: 0 0 0.5rem 0;
}

.page-subtitle {
  color: var(--text-sub);
  font-size: 0.95rem;
  margin: 0;
}

.account-container {
  width: 100%;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.card {
  background: var(--bg-white);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-color);
  overflow: hidden;
}

/* 概览卡片 */
.overview-card {
  display: flex;
  align-items: center;
  padding: 2rem;
  gap: 2rem;
}

.profile-section {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.avatar-wrapper {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--primary-gradient);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.avatar-placeholder {
  font-size: 2rem;
  font-weight: bold;
  color: var(--text-inverse);
}

/* 覆写全局 components.css 中 .account-avatar 的蓝紫渐变（scoped 优先级更高） */
.account-avatar {
  background: var(--primary-gradient);
  color: var(--text-inverse);
}

.name-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.user-name {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--text-title);
}

.uid-badge {
  background: var(--surface-muted);
  color: var(--text-body);
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-family: monospace;
}

.meta-text {
  margin: 0;
  color: var(--text-sub);
  font-size: 0.9rem;
}

.vertical-divider {
  width: 1px;
  height: 60px;
  background-color: var(--border-strong);
}

/* 算力资产区域 */
.asset-section {
  flex: 1;
  display: flex;
  align-items: center;
  padding-left: 1.5rem;
  width: 100%;
}

.asset-info {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.asset-label {
  font-size: 0.95rem;
  color: var(--text-body);
  white-space: nowrap;
}

.number {
  font-family: var(--font-num);
  font-size: 2.2rem;
  font-weight: 700;
  color: var(--text-title);
  line-height: 1;
}

.unit {
  font-size: 0.9rem;
  color: var(--text-sub);
}

.push-right {
  margin-left: auto;
}

.sub-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.3rem;
}
.sub-badge {
  background: linear-gradient(135deg, #ffd700, #ffaa00);
  color: #7b5f00;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}
.sub-expire {
  color: var(--text-sub);
  font-size: 0.8rem;
}

/* 安全设置卡片 */
.security-card {
  padding: 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--surface-soft);
}

.card-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.security-list {
  display: flex;
  flex-direction: column;
}

.list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  transition: background-color 0.2s;
}

.list-item:last-child {
  border-bottom: none;
}

.list-item:hover {
  background-color: var(--fill-hover);
}

.item-title {
  font-size: 1rem;
  font-weight: 500;
  color: var(--text-title);
  margin-bottom: 0.4rem;
}

.item-desc {
  font-size: 0.9rem;
  color: var(--text-body);
}

.highlight-text {
  color: var(--text-title);
  font-weight: 500;
}

.text-muted {
  color: var(--text-sub);
}

.item-action {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  min-width: 200px;
  justify-content: flex-end;
}

/* 状态指示器 */
.status-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
}

.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.status-indicator.ok {
  color: var(--success);
}
.status-indicator.ok .dot {
  background-color: var(--success);
}

.status-indicator.warn {
  color: var(--warning);
}
.status-indicator.warn .dot {
  background-color: var(--warning);
}

/* 按钮与操作区 */
.btn {
  padding: 0.6rem 1.2rem;
  border-radius: var(--radius-sm);
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all var(--dur-fast) var(--ease-out);
  font-size: 0.9rem;
}

.btn-primary {
  background: var(--primary-gradient);
  color: #fff;
  box-shadow: var(--shadow-primary);
}
.btn-primary:hover {
  box-shadow: var(--shadow-primary-hover);
  transform: translateY(-1px);
}
.btn-primary:active {
  transform: translateY(0);
}

.btn-primary-outline {
  padding: 0.4rem 1rem;
  border-radius: var(--radius-sm);
  background: var(--primary-soft);
  color: var(--primary-active);
  border: 1px solid transparent;
  cursor: pointer;
  transition: all var(--dur-fast) var(--ease-out);
  font-size: 0.9rem;
}
.btn-primary-outline:hover {
  background: var(--primary-color);
  color: #fff;
}

.btn-outline {
  padding: 0.4rem 1rem;
  border-radius: var(--radius-sm);
  background: var(--bg-white);
  color: var(--text-body);
  border: 1px solid var(--border-strong);
  cursor: pointer;
  transition: all var(--dur-fast) var(--ease-out);
  font-size: 0.9rem;
}
.btn-outline:hover {
  border-color: var(--primary-color);
  color: var(--primary-active);
  background: var(--primary-softer);
}
.btn-outline:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-text {
  background: transparent;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  font-size: 0.9rem;
  transition: color var(--dur-fast) var(--ease-out);
}
.btn-text:hover {
  color: var(--primary-active);
}

.actions-section {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

.btn-danger {
  padding: 0.6rem 2rem;
  background: var(--bg-white);
  border: 1px solid var(--danger);
  color: var(--danger);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all var(--dur-fast) var(--ease-out);
  font-weight: 500;
}
.btn-danger:hover {
  background: var(--danger-soft);
}
.btn-danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 兑换码卡片 */
.redeem-card {
  padding: 1.25rem 1.5rem;
}
.redeem-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}
.redeem-label {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 220px;
  flex: 1;
}
.redeem-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-title);
}
.redeem-hint {
  font-size: 0.85rem;
  color: var(--text-sub);
}
.redeem-form {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.redeem-input {
  width: 220px;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  font-size: 0.95rem;
  outline: none;
  transition: border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out);
}
.redeem-input:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px var(--focus-ring);
}
.redeem-input:disabled {
  background: var(--surface-soft);
  cursor: not-allowed;
}
.redeem-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.redeem-msg {
  font-size: 0.85rem;
  width: 100%;
  margin-top: 0.25rem;
}
.redeem-msg.ok {
  color: var(--success);
}
.redeem-msg.err {
  color: var(--danger);
}

/* 修改密码弹窗 */
.pwd-modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(43, 37, 33, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}
.pwd-modal {
  background: var(--bg-white);
  border-radius: var(--radius-lg);
  padding: 1.5rem;
  width: 360px;
  max-width: calc(100vw - 2rem);
  box-shadow: var(--shadow-xl);
}
.pwd-modal-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-title);
  margin-bottom: 0.25rem;
}
.pwd-modal-hint {
  font-size: 0.85rem;
  color: var(--text-sub);
  margin-bottom: 1rem;
}
.pwd-field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin-bottom: 0.85rem;
}
.pwd-field label {
  font-size: 0.85rem;
  color: var(--text-body);
}
.pwd-field input {
  padding: 0.55rem 0.75rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  font-size: 0.95rem;
  outline: none;
  transition: border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out);
}
.pwd-field input:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px var(--focus-ring);
}
.pwd-field input:disabled {
  background: var(--surface-soft);
}
.pwd-msg {
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
}
.pwd-msg.ok {
  color: var(--success);
}
.pwd-msg.err {
  color: var(--danger);
}
.pwd-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
}
.pwd-actions .btn-primary {
  padding: 0.5rem 1.2rem;
  border-radius: var(--radius-sm);
  background: var(--primary-gradient);
  color: #fff;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
  box-shadow: var(--shadow-primary);
  transition: all var(--dur-fast) var(--ease-out);
}
.pwd-actions .btn-primary:hover {
  box-shadow: var(--shadow-primary-hover);
  transform: translateY(-1px);
}
.pwd-actions .btn-primary:active {
  transform: translateY(0);
}
.pwd-actions .btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}
.pwd-actions .btn-outline {
  padding: 0.5rem 1.2rem;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .overview-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
  }
  .vertical-divider {
    width: 100%;
    height: 1px;
  }
  .asset-section {
    width: 100%;
    padding-left: 0;
  }
  .list-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  .item-action {
    width: 100%;
    justify-content: space-between;
  }
}
</style>